import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['input', 'results']

  connect() {
    this.selectedIndex = -1
    this.debounceTimer = null
    this.closeOnClickOutside = this.closeOnClickOutside.bind(this)
  }

  disconnect() {
    this.clearDebounce()
    document.removeEventListener('click', this.closeOnClickOutside)
  }

  search() {
    this.clearDebounce()
    const query = this.inputTarget.value.trim()

    if (query.length < 2) {
      this.hideResults()
      return
    }

    this.debounceTimer = setTimeout(() => {
      this.fetchResults(query)
    }, 300)
  }

  async fetchResults(query) {
    try {
      const url = new URL('/glossary', window.location.origin)
      url.searchParams.set('q', query)
      url.searchParams.set('format', 'json')

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`)
      }

      const data = await response.json()
      this.renderResults(data.terms || [])
    } catch (error) {
      console.error('Glossary search failed:', error)
      this.hideResults()
    }
  }

  renderResults(terms) {
    this.selectedIndex = -1

    if (terms.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="px-4 py-3 text-sm text-gray-500">
          No terms found. Try a different search.
        </div>
      `
      this.showResults()
      return
    }

    const html = terms.map((term, index) => `
      <a href="${term.url}"
         class="block px-4 py-3 hover:bg-gray-100 border-b border-gray-200 last:border-b-0"
         data-index="${index}"
         data-action="mouseenter->glossary--search#highlightResult click->glossary--search#selectResult">
        <div class="font-medium text-gray-900">${this.escapeHtml(term.term)}</div>
        <div class="text-sm text-gray-500 line-clamp-1">${this.escapeHtml(term.short_definition || '')}</div>
        <div class="text-xs text-gray-400 mt-0.5">${this.escapeHtml(term.category || '')}</div>
      </a>
    `).join('')

    this.resultsTarget.innerHTML = html
    this.showResults()
  }

  handleKeydown(event) {
    const results = this.resultsTarget.querySelectorAll('a[data-index]')

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, results.length - 1)
        this.updateHighlight(results)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1)
        this.updateHighlight(results)
        break
      case 'Enter':
        if (this.selectedIndex >= 0 && results[this.selectedIndex]) {
          event.preventDefault()
          window.location.href = results[this.selectedIndex].href
        }
        break
      case 'Escape':
        this.hideResults()
        break
    }
  }

  highlightResult(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    this.selectedIndex = index
    const results = this.resultsTarget.querySelectorAll('a[data-index]')
    this.updateHighlight(results)
  }

  selectResult(_event) {
    // Let the default link behavior happen
  }

  updateHighlight(results) {
    results.forEach((result, index) => {
      if (index === this.selectedIndex) {
        result.classList.add('bg-gray-100')
      } else {
        result.classList.remove('bg-gray-100')
      }
    })

    // Scroll highlighted item into view
    if (this.selectedIndex >= 0 && results[this.selectedIndex]) {
      results[this.selectedIndex].scrollIntoView({ block: 'nearest' })
    }
  }

  showResults() {
    this.resultsTarget.classList.remove('hidden')
    document.addEventListener('click', this.closeOnClickOutside)
  }

  hideResults() {
    this.resultsTarget.classList.add('hidden')
    this.selectedIndex = -1
    document.removeEventListener('click', this.closeOnClickOutside)
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }

  clearDebounce() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
